/**
 * GpxImportStep - Optional GPX file upload and analysis step
 *
 * Features:
 * - Drag-and-drop file upload
 * - File validation (.gpx, <10MB)
 * - Upload progress indicator
 * - Analysis preview (waypoints, distance, elevation)
 * - Anomaly warnings
 * - Skip option (GPX optional)
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-236 (US-1.20.6-S.3-01: GPX Import Component with Drag-Drop)
 *
 * @component
 */

import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useGpxAnalysis from '../../../../hooks/experiences/useGpxAnalysis';

/**
 * GpxImportStep
 *
 * Optional step for importing GPX file with category-specific analysis.
 */
const GpxImportStep = ({ experienceId: _experienceId, categoryL1Id, onAnalysisComplete, onSkip }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const fileInputRef = useRef(null);

  const { isAnalyzing, analysisResult, error, analyze, reset } = useGpxAnalysis({
    experienceId: _experienceId,
    categoryL1Id
  });

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    if (!file) {return;}

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.gpx')) {
      setValidationError('Only GPX files are supported');
      setTimeout(() => setValidationError(null), 3000);
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      setValidationError('File size must be less than 10MB');
      setTimeout(() => setValidationError(null), 3000);
      return;
    }

    setSelectedFile(file);
    setValidationError(null);
    reset();
  }, [reset]);

  // Handle file input change
  const handleFileInputChange = useCallback(
    (  _e) => {
      const file = _e.target.files?.[0];
      handleFileSelect(file);
    },
    [handleFileSelect]
  );

  // Handle drag and drop
  const handleDragEnter = useCallback((  _e) => {
    _e.preventDefault();
    _e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((  _e) => {
    _e.preventDefault();
    _e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((  _e) => {
    _e.preventDefault();
    _e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (  _e) => {
      _e.preventDefault();
      _e.stopPropagation();
      setIsDragging(false);

      const file = _e.dataTransfer.files?.[0];
      handleFileSelect(file);
    },
    [handleFileSelect]
  );

  // Handle analyze button click
  const handleAnalyze = useCallback(async () => {
    if (!selectedFile) {return;}

    const result = await analyze(selectedFile);

    if (result) {
      onAnalysisComplete(result);
    }
  }, [selectedFile, analyze, onAnalysisComplete]);

  // Handle remove file
  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [reset]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) {return `${bytes} B`;}
    if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} KB`;}
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="gpx-import-step space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Import GPX Route <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Upload a GPX file to automatically extract waypoints, route statistics, and elevation data.
        </p>
      </div>

      {!analysisResult ? (
        <>
          {/* File Upload Area */}
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`
              relative border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : selectedFile
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400'
              }
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".gpx"
              onChange={handleFileInputChange}
              className="hidden"
            />

            {!selectedFile ? (
              <>
                {/* Upload Icon */}
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>

                <p className="text-base font-medium text-gray-700 mb-2">
                  {isDragging ? 'Drop GPX file here' : 'Drag and drop GPX file'}
                </p>

                <p className="text-sm text-gray-500 mb-4">or</p>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Choose File
                </button>

                <p className="mt-4 text-xs text-gray-400">
                  Supported: .gpx files up to 10MB
                </p>
              </>
            ) : (
              <>
                {/* Selected File */}
                <div className="flex items-center justify-center gap-3 mb-4">
                  <svg
                    className="w-10 h-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>

                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    onClick={handleAnalyze}
                    disabled={isAnalyzing}
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAnalyzing ? (
                      <>
                        <svg
                          className="inline-block w-4 h-4 mr-2 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Analyzing...
                      </>
                    ) : (
                      'Analyze GPX'
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    disabled={isAnalyzing}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="flex items-start gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <svg
                className="w-5 h-5 text-amber-600 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">Validation Error</p>
                <p className="text-xs text-amber-700 mt-1">{validationError}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <svg
                className="w-5 h-5 text-red-600 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Analysis Failed</p>
                <p className="text-xs text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Skip Option */}
          <div className="border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Skip GPX import and continue manually
            </button>
          </div>
        </>
      ) : (
        /* Analysis Result Preview */
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-green-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="font-semibold">Analysis Complete!</span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {analysisResult.route_stats?.distance_miles && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">
                  {analysisResult.route_stats.distance_miles}
                </p>
                <p className="text-xs text-gray-600 mt-1">Miles</p>
              </div>
            )}

            {analysisResult.route_stats?.elevation_gain_ft && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">
                  {analysisResult.route_stats.elevation_gain_ft}
                </p>
                <p className="text-xs text-gray-600 mt-1">Elevation Gain (ft)</p>
              </div>
            )}

            {analysisResult.waypoints && (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{analysisResult.waypoints.length}</p>
                <p className="text-xs text-gray-600 mt-1">Waypoints</p>
              </div>
            )}
          </div>

          {/* Anomalies */}
          {analysisResult.anomalies && analysisResult.anomalies.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-900 mb-2">
                {analysisResult.anomalies.length} potential issue
                {analysisResult.anomalies.length !== 1 ? 's' : ''} detected
              </p>
              <ul className="text-xs text-amber-700 space-y-1">
                {analysisResult.anomalies.slice(0, 3).map((anomaly, idx) => (
                  <li key={idx}>• {anomaly.message}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-sm text-gray-600">
            Click <strong>Next</strong> to review and edit waypoints.
          </p>
        </div>
      )}
    </div>
  );
};

GpxImportStep.propTypes = {
  /**
   * Experience ID (or 'new' for creation)
   */
  experienceId: PropTypes.string.isRequired,

  /**
   * L1 Category ID (for strategy selection)
   */
  categoryL1Id: PropTypes.number.isRequired,

  /**
   * Callback when analysis completes
   */
  onAnalysisComplete: PropTypes.func.isRequired,

  /**
   * Callback when user skips GPX import
   */
  onSkip: PropTypes.func.isRequired
};

export default GpxImportStep;
